import { ContentState, EditorState } from "draft-js";
import { Options as ExportOptions, stateToHTML } from "draft-js-export-html";
import { CustomInlineFn, stateFromHTML } from "draft-js-import-html";
import { HTML_FONT_WEIGHT_ENUM } from "../../utils/AppEnumerations";

const TEXT_COLOR_PREFIX = "color-";
const BG_COLOR_PREFIX = "bgcolor-";

const toStyleName = (prefix: string, color: string) =>
  `${prefix}${color.replace(/ /g, "")}`;

const getColorInlineStyles = (
  content: ContentState,
): ExportOptions["inlineStyles"] => {
  const inlineStyles: NonNullable<ExportOptions["inlineStyles"]> = {};
  content.getBlocksAsArray().forEach((block) => {
    block.getCharacterList().forEach((char) => {
      char?.getStyle().forEach((styleName) => {
        if (!styleName || inlineStyles[styleName]) return;
        if (styleName.startsWith(TEXT_COLOR_PREFIX)) {
          inlineStyles[styleName] = {
            style: { color: styleName.slice(TEXT_COLOR_PREFIX.length) },
          };
        } else if (styleName.startsWith(BG_COLOR_PREFIX)) {
          inlineStyles[styleName] = {
            style: {
              backgroundColor: styleName.slice(BG_COLOR_PREFIX.length),
            },
          };
        }
      });
    });
  });
  return inlineStyles;
};

const parseColorInline: CustomInlineFn = (element, { Style }) => {
  if (!(element instanceof HTMLElement) || element.tagName !== "SPAN") {
    return undefined;
  }
  const { color, backgroundColor } = element.style;
  if (color) return Style(toStyleName(TEXT_COLOR_PREFIX, color));
  if (backgroundColor) {
    return Style(toStyleName(BG_COLOR_PREFIX, backgroundColor));
  }
  return undefined;
};

const CONVERTIBLE_STYLE_PROPERTIES = [
  "color",
  "background-color",
  "font-weight",
  "font-style",
  "text-decoration",
];
const IGNORED_COLOR_VALUES = [
  "transparent",
  "inherit",
  "initial",
  "unset",
  "currentcolor",
];

interface ITextStyles {
  color?: string;
  backgroundColor?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
}

const readColor = (value: string): string | undefined =>
  value && !IGNORED_COLOR_VALUES.includes(value.toLowerCase())
    ? value
    : undefined;

const readTextStyles = (style: CSSStyleDeclaration): ITextStyles => {
  const textStyles: ITextStyles = {};
  const color = readColor(style.color);
  const backgroundColor = readColor(style.backgroundColor);
  if (color) textStyles.color = color;
  if (backgroundColor) textStyles.backgroundColor = backgroundColor;
  if (style.fontWeight) {
    textStyles.bold =
      style.fontWeight === HTML_FONT_WEIGHT_ENUM.Bold ||
      style.fontWeight === HTML_FONT_WEIGHT_ENUM.Bolder ||
      Number(style.fontWeight) >= 600;
  }
  if (style.fontStyle)
    textStyles.italic = style.fontStyle !== HTML_FONT_WEIGHT_ENUM.Normal;
  const decorationLine = style.getPropertyValue("text-decoration-line");
  if (decorationLine) {
    textStyles.underline = decorationLine.includes("underline");
    textStyles.strikethrough = decorationLine.includes("line-through");
  }
  return textStyles;
};

const hasTextStyles = (textStyles: ITextStyles) =>
  Object.values(textStyles).some(Boolean);

const wrapTextNode = (textNode: Text, textStyles: ITextStyles) => {
  const doc = textNode.ownerDocument;
  const wrappers: HTMLElement[] = [];
  if (textStyles.color) {
    const span = doc.createElement("span");
    span.style.color = textStyles.color;
    wrappers.push(span);
  }
  if (textStyles.backgroundColor) {
    const span = doc.createElement("span");
    span.style.backgroundColor = textStyles.backgroundColor;
    wrappers.push(span);
  }
  if (textStyles.bold) wrappers.push(doc.createElement("strong"));
  if (textStyles.italic) wrappers.push(doc.createElement("em"));
  if (textStyles.underline) wrappers.push(doc.createElement("u"));
  if (textStyles.strikethrough) wrappers.push(doc.createElement("s"));
  if (!wrappers.length) return;

  textNode.replaceWith(wrappers[0]);
  wrappers.reduce((parent, child) => {
    parent.appendChild(child);
    return child;
  });
  wrappers[wrappers.length - 1].appendChild(textNode);
};

const pushDownTextStyles = (node: Node, inherited: ITextStyles) => {
  if (node instanceof Text) {
    if (node.textContent?.trim() && hasTextStyles(inherited)) {
      wrapTextNode(node, inherited);
    }
    return;
  }
  if (!(node instanceof HTMLElement)) return;

  const { style } = node;
  const textStyles = { ...inherited, ...readTextStyles(style) };
  CONVERTIBLE_STYLE_PROPERTIES.forEach((property) =>
    style.removeProperty(property),
  );
  if (!style.length) node.removeAttribute("style");

  Array.from(node.childNodes).forEach((child) =>
    pushDownTextStyles(child, textStyles),
  );
};

const normalizeTextStyles = (html: string): string => {
  const { body } = new DOMParser().parseFromString(html, "text/html");
  pushDownTextStyles(body, {});
  return body.innerHTML;
};

export const createEditorState = (html: string): EditorState => {
  const contentState = stateFromHTML(normalizeTextStyles(html), {
    customInlineFn: parseColorInline,
  });
  return EditorState.createWithContent(contentState);
};

export const editorStateToHtml = (editorState: EditorState): string => {
  const content = editorState.getCurrentContent();
  return stateToHTML(content, {
    inlineStyles: getColorInlineStyles(content),
  });
};

const RICH_TEXT_ATTRIBUTES: Record<string, string[]> = {
  DIV: [],
  SECTION: [],
  ARTICLE: [],
  HEADER: [],
  FOOTER: [],
  ASIDE: [],
  P: [],
  BR: [],
  H1: [],
  H2: [],
  H3: [],
  H4: [],
  H5: [],
  H6: [],
  STRONG: [],
  B: [],
  EM: [],
  I: [],
  U: [],
  S: [],
  DEL: [],
  CODE: [],
  PRE: [],
  BLOCKQUOTE: [],
  OL: [],
  UL: [],
  LI: [],
  FIGURE: [],
  SPAN: [],
  A: ["href", "target", "rel", "title"],
  IMG: ["src", "alt", "width", "height"],
};

const isConvertibleStyleProperty = (property: string) =>
  CONVERTIBLE_STYLE_PROPERTIES.some((convertible) =>
    property.startsWith(convertible),
  );

export const isRichTextCompatible = (html: string): boolean => {
  const { body } = new DOMParser().parseFromString(html, "text/html");
  return Array.from(body.querySelectorAll("*")).every((element) => {
    const allowedAttributes = RICH_TEXT_ATTRIBUTES[element.tagName];
    if (
      !allowedAttributes ||
      !element
        .getAttributeNames()
        .every(
          (name) =>
            name === HTML_FONT_WEIGHT_ENUM.Style ||
            allowedAttributes.includes(name),
        )
    ) {
      return false;
    }
    if (!(element instanceof HTMLElement)) return true;
    const { style } = element;
    return Array.from({ length: style.length }, (_, index) =>
      style.item(index),
    ).every(
      (property) =>
        isConvertibleStyleProperty(property) ||
        style.getPropertyValue(property) === HTML_FONT_WEIGHT_ENUM.Initial,
    );
  });
};
